import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { CONTENT } from "@/content";
import { BorderBeam } from "@/components/ui/border-beam";
import { Reveal } from "../ui/Reveal";
import { SecHead } from "./SecHead";
import { motion, AnimatePresence } from "framer-motion";

type Props = {
  onCTA: () => void;
};

export function MetodoPreview() {
  const m = CONTENT.metodo;
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % m.pillars.length);
    }, 2500);
    return () => clearInterval(timer);
  }, [m.pillars.length]);

  return (
    <section className="section dark landing-preview" id="metodo">
      <div className="wrap">
        <SecHead label={m.label} title={m.title} intro={m.intro.slice(0, 1)} dark align="split" />
        
        <div className="compass-preview-interactive">
          {/* Ambient Glow */}
          <div className="compass-preview-glow" />

          {/* Huge animated word */}
          <motion.div 
            className="compass-huge-word"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {"COMPASS".split("").map((letter, i) => (
              <motion.span 
                key={i} 
                className={`compass-huge-letter ${index === i ? 'active' : ''}`}
                onClick={() => setIndex(i)}
                whileHover={{ scale: 1.1 }}
              >
                {letter}
              </motion.span>
            ))}
          </motion.div>

          {/* Morphing Pillar Name */}
          <div className="compass-preview-pillar">
            <span className="compass-preview-kicker">Pilar {index + 1}</span>
            <AnimatePresence mode="wait">
              {(() => {
                const p = m.pillars[index];
                if (!p) return null;
                return (
                  <motion.h3
                    key={index}
                    initial={{ y: 20, opacity: 0, filter: "blur(8px)" }}
                    animate={{ y: 0, opacity: 1, filter: "blur(0px)" }}
                    exit={{ y: -20, opacity: 0, filter: "blur(8px)" }}
                    transition={{ duration: 0.4 }}
                    className="compass-preview-name"
                  >
                    {p[1]}
                  </motion.h3>
                );
              })()}
            </AnimatePresence>
          </div>

          <Reveal delay={1}>
            <Link className="btn btn-gold btn-lg preview-link" style={{ marginTop: '32px', position: 'relative', zIndex: 2 }} to="/metodo">
              Ver método completo <span className="arr">→</span>
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export function TrilhasPreview({ onCTA }: Props) {
  const t = CONTENT.trilhas;
  return (
    <section className="section trilhas landing-preview" id="trilhas">
      <div className="wrap">
        <SecHead label={t.label} title={t.title} intro={t.intro.slice(0, 1)} align="split" />
        <div className="preview-trilhas-grid">
          {t.cards.map((card, index) => (
            <Reveal as="article" key={card.key} className="preview-trilha" delay={(Math.min(index + 1, 3)) as 1 | 2 | 3}>
              <span className="band-idx">{String(index + 1).padStart(2, "0")}</span>
              <h3 className="band-name serif">{card.key}</h3>
              {card.flag && <span className="band-flag">{card.flag}</span>}
              <p className="band-promise serif">{card.promise}</p>
            </Reveal>
          ))}
        </div>
        <Reveal className="preview-actions">
          <Link className="btn btn-ghost btn-lg" to="/trilhas">
            Ver trilhas completas <span className="arr">→</span>
          </Link>
          <button className="btn btn-primary btn-lg btn-beam btn-beam-primary" onClick={onCTA}>
            <BorderBeam />
            {t.cta} <span className="arr">→</span>
          </button>
        </Reveal>
      </div>
    </section>
  );
}
